import { type PayloadAction, createSlice } from '@reduxjs/toolkit'

import type { RootState } from '@web/app/store'
import { useDispatchedActions } from '@web/shared/hooks/use-dispatched-actions.hook'
import {
  type TRadiusMode,
  borderRadiusStorage
} from '@web/shared/local-storage/border-radius.storage'
import { testModeStorage } from '@web/shared/local-storage/test-mode.storage'
import {
  type TTheme,
  themeStorage
} from '@web/shared/local-storage/theme.storage'

export interface ISettingsState {
  theme: TTheme
  borderRadius: TRadiusMode
  testMode: boolean
}

const applyThemeToDom = (theme: TTheme): void => {
  document.documentElement.dataset.theme = theme
}

const applyRadiusToDom = (radius: TRadiusMode): void => {
  document.documentElement.dataset.radius = radius
}

const initialState: ISettingsState = {
  theme: themeStorage.getTheme(),
  borderRadius: borderRadiusStorage.getRadius(),
  testMode: testModeStorage.getTestMode()
}

applyThemeToDom(initialState.theme)
applyRadiusToDom(initialState.borderRadius)

export const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    toggleTheme(state) {
      state.theme = state.theme === 'light' ? 'dark' : 'light'
      themeStorage.setTheme(state.theme)
      applyThemeToDom(state.theme)
    },
    toggleBorderRadius(state) {
      state.borderRadius =
        state.borderRadius === 'square' ? 'rounded' : 'square'
      borderRadiusStorage.setRadius(state.borderRadius)
      applyRadiusToDom(state.borderRadius)
    },
    setTestMode(state, action: PayloadAction<boolean>) {
      state.testMode = action.payload
      testModeStorage.setTestMode(state.testMode)
    }
  }
})

export const settingsReducer = settingsSlice.reducer
export const useSettingsActions = () =>
  useDispatchedActions(settingsSlice.actions)

export const selectTheme = (state: RootState): TTheme => state.settings.theme
export const selectBorderRadius = (state: RootState): TRadiusMode =>
  state.settings.borderRadius
export const selectTestMode = (state: RootState): boolean =>
  state.settings.testMode
